import $ from 'jquery'
import { Util } from 'bootstrap'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'fileinput'
const DATA_KEY = `opp.${NAME}`
const EVENT_KEY = '.' + DATA_KEY
const DATA_API_KEY = '.data-api'
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Default = {
  maxfilesize: 0,
  maxfilesizehuman: false,
  preview: false
}

const DefaultType = {
  maxfilesize: 'number',
  maxfilesizehuman: '(boolean|string)',
  preview: '(boolean|string|element)'
}

const Selector = {
  DATA_TOGGLE: '[data-toggle="file-input"]',
  FILE_INPUT: '.custom-file-input',
  FILE_LABEL: '.custom-file-label'
}

const Event = {
  CHANGE_DATA_API: `change${EVENT_KEY}${DATA_API_KEY}`
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class FileInput {
  constructor(element, config) {
    this._config = this._getConfig(config)

    this._element = element
    this._input = this._getInput()
    this._label = this._getLabel()
    this._placeholder = this._getPlaceholder()
    this._preview = this._config.preview ? this._getPreview() : null
    this._originalPreviewSrc = this._preview ? this._preview.currentSrc : null
  }

  // Public

  show() {
    this._validateFile()
    // N.B. update methods may check validity
    this._updateLabel()
    this._updatePreview()
  }

  // Private

  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }

    if (typeof config.maxfilesize === 'string') {
      config.maxfilesize = parseInt(config.maxfilesize, 10) || 0
    }

    if (config.maxfilesize > 0 && !config.maxfilesizehuman) {
      config.maxfilesizehuman = config.maxfilesize + ' bytes'
    }

    Util.typeCheckConfig(NAME, config, DefaultType)

    return config
  }

  _getInput() {
    return this._element.querySelector(Selector.FILE_INPUT)
  }

  _getLabel() {
    return this._element.querySelector(Selector.FILE_LABEL)
  }

  _getPlaceholder() {
    return $(this._label).children()
  }

  _getPreview() {
    let elem

    if (Util.isElement(this._config.preview)) {
      elem = this._config.preview[0] || this._config.preview
    } else {
      elem = document.querySelector(this._config.preview)
    }

    return elem
  }

  _getFile() {
    return this._input.files[0]
  }

  _validateFile() {
    // Always clear custom validity
    this._input.setCustomValidity('')

    const limit = this._config.maxfilesize
    if (limit <= 0) {
      return
    }

    const file = this._getFile()
    if (!file) {
      return
    }

    if (file.size > limit) {
      const size = this._config.maxfilesizehuman
      this._input.setCustomValidity(`The selected file must not be larger than ${size}`)
    }
  }

  _updateLabel() {
    if (!this._label) {
      return
    }

    const file = this._getFile()
    if (file) {
      $(this._label).text(file.name)
    } else {
      $(this._label).append(this._placeholder)
    }
  }

  _updatePreview() {
    if (!this._preview) {
      return
    }

    const file = this._getFile()
    if (file) {
      if (this._input.validity.valid) {
        const reader = new FileReader()

        reader.addEventListener('load', () => {
          this._preview.src = reader.result
          $(this._preview).closest('.collapse').collapse('show')
        }, false)

        reader.readAsDataURL(file)
      } else {
        // N.B. report validity only after preview/collapse is hidden to avoid
        // causing the validation message to appear out-of-place.
        const collapse = $(this._preview).closest('.collapse')[0]
        if ($(collapse).is('.show')) {
          $(collapse)
            .one('hidden.bs.collapse', () => this._reportValidity())
            .collapse('hide')
        } else {
          this._reportValidity()
        }
      }
    } else if (this._originalPreviewSrc) {
      this._preview.src = this._originalPreviewSrc
      $(this._preview).closest('.collapse').collapse('show')
    }
  }

  _reportValidity() {
    if (this._getFile()) {
      this._input.reportValidity()
    }
  }

  // Static

  static _jQueryInterface(config, ...args) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)
      const _config = {
        ...Default,
        ...$(this).data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new FileInput(this, _config)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

FileInput._jQueryInterface.call($(Selector.DATA_TOGGLE), 'show')

$(document).on(Event.CHANGE_DATA_API, Selector.DATA_TOGGLE, function () {
  FileInput._jQueryInterface.call($(this), 'show')
})

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = FileInput._jQueryInterface
$.fn[NAME].Constructor = FileInput
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return FileInput._jQueryInterface
}

export default FileInput
